import { Modal, Button } from '@cloudscape-design/components';
import { signInWithRedirect } from 'aws-amplify/auth';
import React from 'react';

import styles from './LoginModal.module.css';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const [loadingProvider, setLoadingProvider] = React.useState<null | 'amazon'>(null);

  const handleAmazonLogin = async (): Promise<void> => {
    try {
      setLoadingProvider('amazon');
      await signInWithRedirect({ provider: 'Amazon' });
      // Redirect happens; no need to unset loading here.
    } catch (error) {
      console.error('Error signing in:', error);
      setLoadingProvider(null);
    }
  };

  const ProviderButton: React.FC<{
    onClick?: () => void;
    disabled?: boolean;
    icon: React.ReactNode;
    label: string;
    brand: 'amazon' | 'google' | 'facebook';
    comingSoon?: boolean;
    loading?: boolean;
  }> = ({ onClick, disabled, icon, label, brand, comingSoon, loading }) => {
    const isDisabled = disabled || comingSoon || loading;
    return (
      <button
        type="button"
        onClick={isDisabled ? undefined : onClick}
        className={[
          styles.providerButton,
          styles[`brand_${brand}`],
          isDisabled ? styles.isDisabled : '',
          loading ? styles.isLoading : '',
        ].join(' ')}
        aria-disabled={isDisabled}
        disabled={isDisabled}
      >
        <span className={styles.iconWrap} aria-hidden="true">{icon}</span>
        <span className={styles.label}>{label}</span>
        {loading && <span className={styles.spinner} aria-hidden="true" />}
        {comingSoon && <span className={styles.badge}>Coming soon</span>}
      </button>
    );
  };

  return (
    <Modal
      visible={isOpen}
      onDismiss={onClose}
      header={null}
      closeAriaLabel="Close sign-in modal"
      size="medium"
    >
      <div className={styles.panel}>
        <div className={styles.header}>
          <h2 className={styles.title}>Welcome back</h2>
          <p className={styles.subtitle}>
            Sign in to follow creators, save favorites, and join the crafting community.
          </p>
        </div>

        <div className={styles.buttons}>
          <ProviderButton
            brand="amazon"
            label="Continue with Amazon"
            onClick={handleAmazonLogin}
            loading={loadingProvider === 'amazon'}
            icon={<AmazonIcon />}
          />
          <ProviderButton
            brand="google"
            label="Continue with Google"
            comingSoon
            disabled
            icon={<GoogleIcon />}
          />
          <ProviderButton
            brand="facebook"
            label="Continue with Facebook"
            comingSoon
            disabled
            icon={<FacebookIcon />}
          />
        </div>

        <div className={styles.footer}>
          <p className={styles.disclaimer}>
            By continuing, you agree to our{' '}
            <a href="/terms" className={styles.link}>Terms</a> and{' '}
            <a href="/privacy" className={styles.link}>Privacy Policy</a>.
          </p>
          <Button variant="link" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </Modal>
  );
};

/* --- Minimal, crisp SVG icons (inline for no extra deps) --- */
// LoginModal.tsx (replace AmazonIcon with this)
const AmazonIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    {/* monogram “a” */}
    <path fill="currentColor"
          d="M11.2 4.2c-2.1 0-3.6 1.2-3.6 3 0 1.2.8 2.2 2.1 2.6 1.1.4 1.3.6 1.3 1 0 .6-.4.9-1.2.9-.7 0-1.6-.3-2.3-.7-.2-.1-.4 0-.4.2v1.5c0 .2.1.3.3.4.9.3 1.6.5 2.5.5 2.1 0 3.5-1.2 3.5-3.1 0-1.5-.7-2.4-2.2-2.8-1.1-.3-1.2-.5-1.2-.9 0-.4.3-.8 1.1-.8.7 0 1.3.2 1.9.5.2.1.4 0 .4-.2V4.8c0-.2-.1-.3-.3-.4-.7-.2-1.3-.2-1.9-.2z" />
    {/* amazon smile */}
    <path fill="#FF9900"
          d="M21 16.5c-3.1 2.2-7.4 3.4-11.6 3.4-4.5 0-8.3-1.3-9.4-2.6-.2-.2.1-.5.4-.4 3 1.4 6.2 2.1 9.8 2.1 3.8 0 7.3-.8 10-2.3.4-.2.8.3.4.6z" />
    <path fill="#FF9900"
          d="M22.2 14.9c-.3-.3-1.9-1.6-2.6-1.9-.2-.1-.5-.1-.7.1-.9.8-2.2 1.6-3.6 2.1-.2.1-.2.3 0 .4.9.3 2 .7 2.9 1.1.2.1.5 0 .6-.1.8-.7 2.1-2 2.8-2.5.2-.2.2-.5.1-.7z" />
  </svg>
);


const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.9 0 7.4 1.4 10.1 3.7l6.8-6.8C35.9 2.6 30.3 0 24 0 14.6 0 6.4 5.4 2.5 13.2l7.9 6.1C12.4 13.8 17.7 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.6c-.5 2.6-2 4.8-4.2 6.3v5.2h6.8c4-3.6 6.3-8.9 6.3-15.8z"/>
    <path fill="#FBBC05" d="M10.4 28.5a14.6 14.6 0 010-9l-7.9-6.2A24 24 0 000 24c0 3.9.9 7.6 2.5 10.8l7.9-6.3z"/>
    <path fill="#34A853" d="M24 48c6.3 0 11.6-2.1 15.4-5.7l-6.8-5.2c-1.9 1.3-4.4 2.1-8.6 2.1-6.3 0-11.6-4.3-13.5-10.1l-7.9 6.3C6.4 42.6 14.6 48 24 48z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#1877F2" d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.435H7.078V12.07h3.047V9.412c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.235 2.686.235v2.953h-1.514c-1.492 0-1.956.931-1.956 1.887v2.25h3.328l-.532 3.495h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
  </svg>
);
